import { useState } from "react";
import { Button, ClickAwayListener, Stack, Tooltip, useMediaQuery } from "@mui/material";
import { WarningAmber as WarningIcon } from "@mui/icons-material";
import _ from "lodash";
import { areAllVisibleSelected, toggleVisibleOptions } from "./selectAllOptions";

const CLOSED = { open: false, pinned: false };

/**
 * Disabled buttons get no pointer events, so the tooltip hangs from a wrapper.
 * Hover opens it and leaving closes it; a click or a tap pins it open until the next click elsewhere,
 * since on touch screens the emulated mouseleave right after the tap would close it at once.
 * It opens over the search field (left of the button from `sm` up, above it on phones), never over
 * the options or the dialog's close button.
 */
const OverLimitSelectAll = ({ message }) => {
  const [tooltip, setTooltip] = useState(CLOSED);
  const hoverOpen = () => setTooltip((current) => ({ ...current, open: true }));
  const hoverClose = () => setTooltip((current) => (current.pinned ? current : CLOSED));
  const pinOpen = () => setTooltip({ open: true, pinned: true });
  const close = () => setTooltip(CLOSED);
  const besideSearch = useMediaQuery("(min-width:600px)");

  return (
    <ClickAwayListener onClickAway={close}>
      <Tooltip title={message} arrow placement={besideSearch ? "left" : "top"} open={tooltip.open} onOpen={hoverOpen} onClose={hoverClose} disableTouchListener>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ cursor: "help" }}
          onClick={pinOpen}
        >
          <Button variant="text" disabled>
            Todas
          </Button>
          <WarningIcon color="warning" fontSize="small" />
        </Stack>
      </Tooltip>
    </ClickAwayListener>
  );
};

/**
 * "Todas" / "Ninguna" for the options of a set template.
 * Acts on the visible options only, so it follows the search filter.
 * `canSelectAll` lets a caller with a selection limit disable the select action when it doesn't fit;
 * `overLimitMessage` explains why, in a tooltip over the disabled button and its warning icon.
 */
const SelectAllButton = ({ selectedIds, visibleIds, onChange, canSelectAll = () => true, overLimitMessage }) => {
  if (_.isEmpty(visibleIds)) return null;

  const allSelected = areAllVisibleSelected(selectedIds, visibleIds);
  const nextSelection = toggleVisibleOptions(selectedIds, visibleIds);

  if (!allSelected && !canSelectAll(nextSelection)) return <OverLimitSelectAll message={overLimitMessage} />;

  return (
    <Button variant="text" onClick={() => onChange(nextSelection)}>
      {allSelected ? "Ninguna" : "Todas"}
    </Button>
  );
};

export default SelectAllButton;
